/**
 * Check-ins (TIMELINE R25, decisions 1, 2 and 7): what the form pre-fills, what the server refuses,
 * and what the map expected on the day, kept for the debrief. Web and Claude share these, so a
 * check-in means the same thing wherever it is typed.
 */
import { contract, core, debt as debtNs, parseDay, plan as planNs, type Decimal } from '@map-my-money/engine';
import { compare, type Comparison } from './compare.ts';
import { goalsView } from './goals.ts';
import { money } from './money.ts';
import { nowState } from './now.ts';
import { readableDate } from './stamp.ts';
import { checkInFlowIssues, checkInFlowRows, checkInWindow, expectedFlows, fromNowOn, type CheckInFlow, type CheckInFlowIssue, type CheckInFlowsBody, type MapChange } from './flows.ts';
import { decode, planAsOf, runForecast, type WaypointInput, type WirePlan } from './run.ts';

/** A field of a check-in saved from an untouched pre-fill: kept as an estimate, never an anchor. */
export type EstimateField = 'cash' | 'investments' | `debt:${string}`;

export const CHECK_IN_LABELS = {
  cash: 'Cash',
  cashHint: 'Current or checking accounts, savings accounts and cash: everything you could spend without selling anything.',
  investments: 'Investments',
  investmentsHint: 'ISAs, pensions, shares and funds at today’s value.',
  debt: 'Owed on',
  /** A first check-in's gap that no payment explains: usually the setup balances were a guess, not money in or out. */
  startingEstimate: 'Difference from your starting estimate',
} as const;

export interface CheckInDebt {
  readonly id: string;
  readonly label: string;
  /** What the map expects it to owe at the end of the day, positive. */
  readonly balance: string;
  readonly mortgage: boolean;
}

export interface CheckInPrefill {
  readonly date: string;
  readonly cash: string;
  readonly investments: string;
  readonly net_worth: string;
  readonly debts: readonly CheckInDebt[];
}

const isEstimateField = (field: string): field is EstimateField => field === 'cash' || field === 'investments' || /^debt:.+/.test(field);

/** Every active debt still owing at the end of `date` (mortgages included), what the map expects it to owe. */
export function owedDebts(wire: WirePlan, date: string, today: string = date, waypoints: readonly WaypointInput[] = []): CheckInDebt[] {
  const plan = planAsOf(decode(wire), waypoints, date);
  const day = parseDay(date);
  const now = parseDay(today);
  const properties = planNs.propertyIds(plan);
  return plan.transactions
    .filter((t) => t.type === 'debt' && t.isActive)
    .map((t) => ({ t, owed: core.abs(debtNs.remainingBalance(t, day, now)) }))
    .filter(({ t, owed }) => core.gt(owed, core.ZERO) && day >= (t.debtStartDate ?? t.startDate))
    .map(({ t, owed }) => ({ id: t.id, label: t.label, balance: money(owed), mortgage: t.linkedAssetId !== null && properties.has(t.linkedAssetId) }));
}

/** A check-in that is still being made, carrying step 1's rows: it anchors nothing, so the map plus the rows is what shows. */
const draftWith = (date: string, flows: readonly CheckInFlow[]): WaypointInput => ({
  id: '~step-1', date, balances: { cash: '0', investments: '0' }, createdAt: '~', estimates: ['cash', 'investments'], flows, reviewedFrom: date,
});

/**
 * What the form pre-fills for a check-in on `date`: the map's position at the end of that day, from the
 * check-ins before it, plus what step 1 says happened (R27) when `flows` are given.
 */
export function checkInPrefill(wire: WirePlan, waypoints: readonly WaypointInput[], date: string, today: string = date, flows?: readonly CheckInFlow[]): CheckInPrefill {
  const earlier = [...waypoints.filter((w) => w.date < date), ...(flows ? [draftWith(date, flows)] : [])];
  const now = nowState(runForecast({ plan: wire, waypoints: earlier, asOf: date }, 1));
  return { date, cash: money(now.cash), investments: money(now.investments), net_worth: money(now.netWorth), debts: owedDebts(wire, date, today, earlier) };
}

export interface CheckInBody {
  readonly date: string;
  readonly balances: { readonly cash?: string; readonly investments?: string };
  readonly debts?: Readonly<Record<string, string>>;
  readonly estimates?: readonly string[];
  /** What happened since the last check-in (R27): only the exceptions. Absent: step 1 was not reviewed. */
  readonly flows?: CheckInFlowsBody;
}

export interface CheckInIssue {
  readonly path: string;
  readonly reason: 'future-date' | 'missing-cash' | 'missing-investments' | `missing-debt:${string}` | 'unknown-debt' | 'unknown-estimate' | CheckInFlowIssue['reason'];
  readonly message: string;
}

/**
 * R25: why a check-in cannot be saved, or nothing. It needs cash, investments and a balance for every
 * debt still owing on its date, and cannot be dated after the person's today. Nothing missing is ever
 * stored as £0.
 */
export function checkInIssues(wire: WirePlan, body: CheckInBody, today: string, waypoints: readonly WaypointInput[] = []): CheckInIssue[] {
  const issues: CheckInIssue[] = [];
  if (body.date > today) issues.push({ path: '/date', reason: 'future-date', message: `A check-in can’t be dated after today (${readableDate(today)}): it records what you actually had.` });
  if (body.balances.cash === undefined) issues.push({ path: '/balances/cash', reason: 'missing-cash', message: 'Add your cash total to save this check-in.' });
  if (body.balances.investments === undefined) {
    issues.push({ path: '/balances/investments', reason: 'missing-investments', message: 'Add your investments total to save this check-in (type 0 if you have none).' });
  }
  const owed = owedDebts(wire, body.date > today ? today : body.date, today);
  for (const d of owed) {
    if (body.debts?.[d.id] === undefined) issues.push({ path: `/debts/${d.id}`, reason: `missing-debt:${d.id}`, message: `Add what you owe on ${d.label} to save this check-in (debt ${d.id}).` });
  }
  const known = new Set(decode(wire).transactions.filter((t) => t.type === 'debt').map((t) => t.id));
  for (const id of Object.keys(body.debts ?? {})) {
    if (!known.has(id)) issues.push({ path: `/debts/${id}`, reason: 'unknown-debt', message: `${id} is not a debt in this plan.` });
  }
  for (const field of body.estimates ?? []) {
    if (!isEstimateField(field) || (field.startsWith('debt:') && body.debts?.[field.slice(5)] === undefined)) {
      issues.push({ path: '/estimates', reason: 'unknown-estimate', message: `${field} is not a field of this check-in.` });
    }
  }
  if (body.flows && body.date <= today) issues.push(...checkInFlowIssues(wire, waypoints, body.date, body.flows, today));
  return issues;
}

export interface CheckInExpectation {
  readonly date: string;
  /** What the map expected at the end of that day, from the check-ins before it. */
  readonly expected: {
    readonly net_worth: string;
    readonly cash: string;
    readonly investments: string;
    readonly debts: Readonly<Record<string, string>>;
  };
  /** Every goal's date as projected the moment before the check-in was saved. */
  readonly goals: readonly { readonly id: string; readonly label: string; readonly eta: string | null; readonly achieved: boolean }[];
}

/** What a new check-in is compared with later: the map's expectation for its day, and the goal dates just before it. */
export function checkInExpectation(wire: WirePlan, waypointsBefore: readonly WaypointInput[], date: string, today: string): CheckInExpectation {
  const prefill = checkInPrefill(wire, waypointsBefore, date, today);
  const goals = goalsView({ plan: wire, waypoints: waypointsBefore, asOf: today }).goals;
  return {
    date,
    expected: {
      net_worth: prefill.net_worth,
      cash: prefill.cash,
      investments: prefill.investments,
      debts: Object.fromEntries(prefill.debts.map((d) => [d.id, d.balance])),
    },
    goals: goals.map((g) => ({ id: g.id, label: g.label, eta: g.eta, achieved: g.achieved })),
  };
}

export type CheckInCauseKind = 'added' | 'changed' | 'skipped' | 'markets' | 'debt' | 'not-on-map' | 'balance';

/** One named part of a check-in's gap (R27); together they add up to `difference` to the penny. */
export interface CheckInCause {
  readonly kind: CheckInCauseKind;
  /** The payment's label as it was shown, the debt's label, or the balance's. */
  readonly label: string;
  /** The item or debt id, `cash`/`investments`/`debt:<id>` for a balance, null for markets and not on your map. */
  readonly item: string | null;
  readonly date: string | null;
  /** What it did to net worth against the map: negative when it left you lower. */
  readonly effect: string;
  /** For a payment: what was expected (null when added) and what was paid. */
  readonly expected?: string | null;
  readonly actual?: string;
  /** On a first check-in, the part not on your map is the difference from the setup balances. */
  readonly starting_estimate?: true;
}

export interface CheckInBalanceChange {
  readonly field: EstimateField;
  /** `Cash`, `Investments` or the debt's own label. */
  readonly label: string;
  readonly expected: string;
  readonly actual: string;
  /** What this balance did to net worth against the map: negative when cash or investments came in lower, or a debt higher. */
  readonly effect: string;
  readonly estimate: boolean;
}

/** What a saved check-in tells the person: where net worth is against the map, which balance made the gap, and how the goals moved. */
export interface CheckInResult {
  readonly date: string;
  readonly net_worth: string;
  /** The expectation stored with the check-in, goal dates before it included. */
  readonly expected: CheckInExpectation;
  readonly difference: string;
  readonly balances: readonly CheckInBalanceChange[];
  /** Whether step 1 was reviewed (R27): with it the gap is named by payment, markets and what isn't on the map; without, by balance. */
  readonly reviewed: boolean;
  readonly causes: readonly CheckInCause[];
  /** What the map expected once step 1 is counted: step 2's pre-fill. */
  readonly with_flows: { readonly net_worth: string; readonly cash: string; readonly investments: string; readonly debts: Readonly<Record<string, string>> };
  /** How many payments step 1 showed, by what the person said. */
  readonly flow_counts: Readonly<Record<'confirmed' | 'changed' | 'skipped' | 'added', number>>;
  /** The forecast before vs after the check-in, every goal's date before and after included. */
  readonly impact: Comparison;
}

/**
 * The result of a check-in just saved (REST and log_waypoint): its net worth as the check-in counts it
 * (the typed cash, investments and debts; property and vehicles from the map), the gap to what the map expected, each balance's part in it,
 * and how the forecast and every goal moved (with `after`, the plan a "from now on" change saved with it).
 * Mortgages are typed but valued with their property, so their effect on net worth is zero.
 */
export function checkInResult(
  wire: WirePlan, waypointsBefore: readonly WaypointInput[], waypoint: WaypointInput, expectation: CheckInExpectation, today: string, after: WirePlan = wire,
): CheckInResult {
  const plan = decode(wire);
  const day = parseDay(waypoint.date);
  const decimal = (value: string | undefined) => (value === undefined ? core.ZERO : contract.decodeDecimal(value));
  const typedDebts = Object.entries(waypoint.debts ?? {});
  const snapshot = planNs.checkInBalances(
    plan,
    { cash: decimal(waypoint.balances.cash), investments: decimal(waypoint.balances.investments), debtBalances: new Map(typedDebts.map(([id, owed]) => [id, core.abs(decimal(owed))])) },
    day,
    { now: parseDay(today) },
  );
  const netWorth = planNs.snapshotNetWorth(snapshot);
  const estimates = new Set(waypoint.estimates ?? []);
  const change = (field: EstimateField, label: string, expected: string, actual: string, sign: 1 | -1 | 0): CheckInBalanceChange => {
    const gap = core.sub(decimal(actual), decimal(expected));
    return { field, label, expected, actual, effect: money(sign === 0 ? core.ZERO : sign === 1 ? gap : core.negate(gap)), estimate: estimates.has(field) };
  };
  const mortgages = new Set(owedDebts(wire, waypoint.date, today).filter((d) => d.mortgage).map((d) => d.id));
  const labels = new Map(plan.transactions.map((t) => [t.id, t.label]));
  const balances = [
    change('cash', CHECK_IN_LABELS.cash, expectation.expected.cash, money(snapshot.cash), 1),
    change('investments', CHECK_IN_LABELS.investments, expectation.expected.investments, money(snapshot.investments), 1),
    ...typedDebts.map(([id, owed]) => change(`debt:${id}`, labels.get(id) ?? id, expectation.expected.debts[id] ?? '0', money(core.abs(decimal(owed))), mortgages.has(id) ? 0 : -1)),
  ];
  const difference = core.sub(netWorth, decimal(expectation.expected.net_worth));
  const named = checkInCauses(wire, waypointsBefore, waypoint, expectation, today, difference, mortgages, labels);
  const rows = waypoint.flows ?? [];
  return {
    date: waypoint.date,
    net_worth: money(netWorth),
    expected: expectation,
    difference: money(difference),
    balances,
    reviewed: named.reviewed,
    causes: named.causes,
    with_flows: named.withFlows,
    flow_counts: {
      confirmed: rows.filter((r) => r.status === 'confirmed').length,
      changed: rows.filter((r) => r.status === 'changed').length,
      skipped: rows.filter((r) => r.status === 'skipped').length,
      added: rows.filter((r) => r.status === 'added').length,
    },
    impact: compare(wire, after, waypointsBefore, [waypoint, ...waypointsBefore], today),
  };
}

/**
 * R27's split of the gap: each changed, skipped and added payment in date order, worth what it moves
 * the expected net worth when counted after the ones before it; then markets, each debt not linked to
 * a property, and "not on your map", the rest (typed cash against the map with the flows, which
 * also takes any rounding; on a first check-in, the difference from the starting estimate). Without
 * step 1, by balance.
 */
function checkInCauses(
  wire: WirePlan, before: readonly WaypointInput[], waypoint: WaypointInput, expectation: CheckInExpectation, today: string, difference: Decimal,
  mortgages: ReadonlySet<string>, labels: ReadonlyMap<string, string>,
): { reviewed: boolean; causes: CheckInCause[]; withFlows: CheckInResult['with_flows'] } {
  const decimal = (value: string | undefined) => (value === undefined ? core.ZERO : contract.decodeDecimal(value));
  const reviewed = waypoint.reviewedFrom !== undefined && waypoint.reviewedFrom !== null;
  const first = !before.some((w) => w.date < waypoint.date);
  const rows = reviewed ? (waypoint.flows ?? []) : [];
  const exceptions = [
    ...rows.filter((r) => r.status === 'changed'),
    ...rows.filter((r) => r.status === 'skipped'),
    ...rows.filter((r) => r.status === 'added'),
  ].map((r, i) => ({ r, order: r.status === 'added' ? r.seq : r.occurrence, i }))
    .sort((a, b) => (a.r.date !== b.r.date ? (a.r.date < b.r.date ? -1 : 1) : a.order - b.order || a.i - b.i))
    .map((x) => x.r);
  const confirmedRows = rows.filter((r) => r.status === 'confirmed');
  let previous = decimal(expectation.expected.net_worth);
  let after: CheckInPrefill | null = null;
  const flowCauses: CheckInCause[] = [];
  for (const [i, r] of exceptions.entries()) {
    after = checkInPrefill(wire, before, waypoint.date, today, [...confirmedRows, ...exceptions.slice(0, i + 1)]);
    const value = decimal(after.net_worth);
    flowCauses.push({ kind: r.status as CheckInCauseKind, label: r.label, item: r.item, date: r.date, effect: money(core.sub(value, previous)), expected: r.expected, actual: r.actual });
    previous = value;
  }
  const withFlows = after
    ? { net_worth: after.net_worth, cash: after.cash, investments: after.investments, debts: Object.fromEntries(after.debts.map((d) => [d.id, d.balance])) }
    : expectation.expected;
  const typedDebts = waypoint.debts ?? {};
  const debtEffects = Object.entries(withFlows.debts)
    .filter(([id]) => !mortgages.has(id))
    .map(([id, owed]) => ({ id, effect: typedDebts[id] === undefined ? core.ZERO : core.sub(decimal(owed), core.abs(decimal(typedDebts[id]))) }));
  const markets = core.sub(decimal(waypoint.balances.investments), decimal(withFlows.investments));
  const flowsTotal = core.sub(decimal(withFlows.net_worth), decimal(expectation.expected.net_worth));
  const rest = core.sub(core.sub(core.sub(difference, flowsTotal), markets), debtEffects.reduce<Decimal>((t, d) => core.add(t, d.effect), core.ZERO));
  const causes: CheckInCause[] = reviewed
    ? [
        ...flowCauses,
        { kind: 'markets', label: CHECK_IN_LABELS.investments, item: null, date: null, effect: money(markets) },
        ...debtEffects.map((d): CheckInCause => ({ kind: 'debt', label: labels.get(d.id) ?? d.id, item: d.id, date: null, effect: money(d.effect) })),
        first
          ? { kind: 'not-on-map', label: CHECK_IN_LABELS.startingEstimate, item: null, date: null, effect: money(rest), starting_estimate: true }
          : { kind: 'not-on-map', label: CHECK_IN_LABELS.cash, item: null, date: null, effect: money(rest) },
      ]
    : [
        { kind: 'balance', label: CHECK_IN_LABELS.cash, item: 'cash', date: null, effect: money(rest) },
        { kind: 'balance', label: CHECK_IN_LABELS.investments, item: 'investments', date: null, effect: money(markets) },
        ...debtEffects.map((d): CheckInCause => ({ kind: 'balance', label: labels.get(d.id) ?? d.id, item: `debt:${d.id}`, date: null, effect: money(d.effect) })),
      ];
  return { reviewed, causes, withFlows };
}

/** What saving a validated check-in writes (server, the web's mock and the playground alike). */
export interface PreparedCheckIn {
  readonly expectation: CheckInExpectation;
  /** The first day step 1 covered; null when it was not reviewed or there was nothing to review. */
  readonly reviewedFrom: string | null;
  readonly flows: CheckInFlow[] | null;
  /** The map change saved with it as one plan version ("from now on", "every month"), or null. */
  readonly change: { readonly plan: WirePlan; readonly changesFrom: Readonly<Record<string, string>>; readonly changes: readonly MapChange[] } | null;
}

/** A validated check-in on `before` (R25, R27): what the map expected, step 1's rows for every payment shown, and any map change. */
export function prepareCheckIn(wire: WirePlan, before: readonly WaypointInput[], body: CheckInBody, today: string): PreparedCheckIn {
  const expectation = checkInExpectation(wire, before, body.date, today);
  const window = body.flows ? checkInWindow(wire, before, body.date) : null;
  const reviewed = window !== null && !window.empty;
  return {
    expectation,
    reviewedFrom: reviewed ? window.from : null,
    flows: reviewed ? checkInFlowRows(expectedFlows(wire, before, window.from, window.to, today), body.flows!) : null,
    change: body.flows ? fromNowOn(wire, body.flows, body.date) : null,
  };
}

/** Which of the typed fields equal the pre-fill and were left untouched: the client says which it touched. */
export const untouchedFields = (prefill: CheckInPrefill, touched: ReadonlySet<string>): EstimateField[] =>
  (['cash', 'investments', ...prefill.debts.map((d) => `debt:${d.id}` as const)] as EstimateField[]).filter((f) => !touched.has(f));
